import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * A thin route-change progress bar pinned to the top of the viewport.
 *
 * It trickles to 90% on a path change, then jumps to 100% and fades. The
 * trickle is deliberately not tied to real load progress — nothing here knows
 * how long a page will take, and a bar that stalls at a truthful 40% feels
 * broken, where one that keeps creeping reads as "working".
 */
export function NavigationProgress() {
  const { pathname } = useLocation();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const timers = useRef([]);

  useEffect(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];

    setVisible(true);
    setProgress(0);

    timers.current.push(setTimeout(() => setProgress(35), 30));
    timers.current.push(setTimeout(() => setProgress(70), 220));
    timers.current.push(setTimeout(() => setProgress(90), 500));
    timers.current.push(setTimeout(() => setProgress(100), 720));
    timers.current.push(setTimeout(() => setVisible(false), 1100));

    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, [pathname]);

  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-x-0 top-0 z-[70]" style={{ height: 2 }}>
      <div
        className="h-full"
        style={{
          width: `${progress}%`,
          background: 'rgb(var(--ink))',
          opacity: visible ? 1 : 0,
          transition: 'width 0.4s ease, opacity 0.35s ease',
        }}
      />
    </div>
  );
}

export default NavigationProgress;
